import React, { useState, useRef, useEffect } from 'react';

interface DropdownOption {
  label: string;
  value: string;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

interface ConsolidatedButtonProps {
  label: string;
  options: DropdownOption[];
  variant?: 'blue' | 'green' | 'purple';
  disabled?: boolean;
}

const ConsolidatedButton: React.FC<ConsolidatedButtonProps> = ({
  label,
  options,
  variant = 'blue',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loadingOption, setLoadingOption] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setLoadingOption(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Monitor loading states and close dropdown when loading completes
  useEffect(() => {
    if (loadingOption) {
      const option = options.find(opt => opt.value === loadingOption);
      if (option && !option.loading) {
        // Loading completed, close dropdown after a short delay
        const timer = setTimeout(() => {
          setIsOpen(false);
          setLoadingOption(null);
        }, 500); // Small delay to show completion
        
        return () => clearTimeout(timer);
      }
    }
  }, [options, loadingOption]);

  const getVariantClasses = () => {
    switch (variant) {
      case 'green':
        return {
          button: 'bg-green-600 hover:bg-green-700 border-green-600',
          dropdown: 'border-green-200',
          option: 'hover:bg-green-50 text-green-700',
        };
      case 'purple':
        return {
          button: 'bg-purple-600 hover:bg-purple-700 border-purple-600',
          dropdown: 'border-purple-200',
          option: 'hover:bg-purple-50 text-purple-700',
        };
      default:
        return {
          button: 'bg-blue-600 hover:bg-blue-700 border-blue-600',
          dropdown: 'border-blue-200',
          option: 'hover:bg-blue-50 text-blue-700',
        };
    }
  };

  const variantClasses = getVariantClasses();
  
  // Check if any option is currently loading
  const isAnyOptionLoading = options.some(option => option.loading);

  const handleOptionClick = (option: DropdownOption) => {
    if (option.disabled || option.loading) return;
    
    // Track this option as potentially loading
    setLoadingOption(option.value);
    option.onClick();
    
    // Don't close dropdown immediately - let the loading state determine this
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || isAnyOptionLoading}
        className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${variantClasses.button}`}
      >
        {isAnyOptionLoading ? (
          <>
            <div className="mr-2 w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Generating...
          </>
        ) : (
          <>
            {label}
            <svg
              className={`ml-2 w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {(isOpen || loadingOption) && (
        <div className={`absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border ${variantClasses.dropdown}`}>
          <div className="py-1">
            {options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleOptionClick(option)}
                disabled={option.disabled || option.loading}
                className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${
                  option.disabled || option.loading
                    ? 'opacity-50 cursor-not-allowed text-gray-400'
                    : variantClasses.option
                }`}
              >
                {option.loading ? (
                  <>
                    <div className="mr-3 w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    {option.icon && <span className="mr-3">{option.icon}</span>}
                    {option.label}
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsolidatedButton;
